import React from "react";
import { Link } from "react-router-dom";
import { Check, Info, Loader2 } from "lucide-react";
import { api } from "../lib/data";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";

/* Le formulaire d'inscription aux workshops heels : il remplace le formulaire Google de
   l'école. Les participantes arrivent directement dans la console (fiche élève + Listes),
   et aucune facture n'est émise — le paiement se fait par TWINT avant la séance. */
export default function WorkshopForm() {
  const [cat, setCat] = React.useState(null);
  const [f, setF] = React.useState({ nom_complet: "", email: "", telephone: "", commentaire: "" });
  const [seances, setSeances] = React.useState([]);
  const [forfaits, setForfaits] = React.useState([]);
  const [etat, setEtat] = React.useState({ envoi: false, err: "", ok: null });
  const envoiEnCours = React.useRef(false);

  useSEO({
    title: "Inscription aux workshops heels — 1000 ARTS Cugy VD",
    description: "Inscris-toi aux workshops heels technique & freestyle de 1000 ARTS à Cugy VD.",
    canonical: "/inscription/workshop",
  });

  React.useEffect(() => {
    api.get("/workshops").then((r) => setCat(r.data)).catch(() => setCat(null));
  }, []);

  const parSemestre = (n) => (cat?.seances || []).filter((s) => Number(s.semestre) === n);
  const auForfait = (n) => forfaits.includes(n);

  const basculerSeance = (id) => setSeances((v) =>
    v.includes(id) ? v.filter((x) => x !== id) : [...v, id]);
  const basculerForfait = (n) => setForfaits((v) => {
    if (v.includes(n)) return v.filter((x) => x !== n);
    // Le forfait couvre tout le semestre : les cases à l'unité n'ont plus lieu d'être.
    setSeances((s) => s.filter((id) => Number(
      (cat?.seances || []).find((x) => x.id === id)?.semestre) !== n));
    return [...v, n];
  });

  const total = React.useMemo(() => {
    if (!cat) return 0;
    const unite = (cat.seances || [])
      .filter((s) => seances.includes(s.id) && !forfaits.includes(Number(s.semestre)))
      .reduce((t, s) => t + Number(s.prix || 0), 0);
    return unite + forfaits.length * Number(cat.forfait_prix || 0);
  }, [cat, seances, forfaits]);

  const valide = f.nom_complet.trim().split(" ").length >= 2
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())
    && f.telephone.trim().length >= 6
    && (seances.length > 0 || forfaits.length > 0);

  const envoyer = async () => {
    if (envoiEnCours.current) return;
    envoiEnCours.current = true;
    setEtat({ envoi: true, err: "", ok: null });
    try {
      const r = await api.post("/inscriptions/workshop", { ...f, seances, forfaits });
      setEtat({ envoi: false, err: "", ok: r.data });
      window.scrollTo(0, 0);
    } catch (e) {
      setEtat({ envoi: false, ok: null,
        err: e?.response?.data?.detail || "L'envoi a échoué. Réessaie dans un instant." });
    } finally {
      envoiEnCours.current = false;
    }
  };

  if (etat.ok) {
    return (
      <div className="pt-32 pb-24 relative overflow-hidden mesh-bg" data-testid="workshop-confirmation">
        <PageDecor />
        <section className="max-w-2xl mx-auto px-6 md:px-12 relative z-10">
          <div className="eyebrow">Workshops heels</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">C'est noté 💃</h1>
          <p className="text-base text-[var(--muted)] mt-5 leading-relaxed">
            Merci {f.nom_complet.split(" ")[0]} ! J'ai bien reçu ton inscription :
          </p>
          <ul className="mt-4 space-y-2 text-sm">
            {(etat.ok.seances || []).map((s) => (
              <li key={s} className="p-3 rounded-xl" style={{ background: "var(--primary-tint)" }}>{s}</li>
            ))}
          </ul>
          <div className="mt-5 p-4 rounded-2xl" style={{ background: "#FFF8E7", border: "1px solid #F5D57A" }}>
            <div className="text-sm" style={{ color: "#8B6914" }}>
              <strong>Ta place n'est validée qu'au paiement.</strong> Merci de verser{" "}
              <strong>CHF {etat.ok.montant}.–</strong> par TWINT au{" "}
              <strong>{etat.ok.twint}</strong>. Tu reçois aussi ce récapitulatif par e-mail.
            </div>
          </div>
          <Link to="/evenements/workshops-heels" className="btn-ghost mt-6 inline-flex">
            Revenir aux workshops
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 relative overflow-hidden mesh-bg" data-testid="page-workshop-form">
      <PageDecor />
      <section className="max-w-3xl mx-auto px-6 md:px-12 relative z-10">
        <div className="eyebrow">Inscription</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 leading-[0.95]">
          Workshops heels
        </h1>
        <p className="text-base text-[var(--muted)] mt-5 leading-relaxed">
          Choisis les workshops auxquels tu veux participer. Chaque séance dure 1h30 et
          s'adresse à tous les niveaux ; le dernier workshop du semestre dure 2h et tu
          repars avec ta vidéo.
        </p>

        {!cat && <div className="mt-8 text-sm text-[var(--muted)]">Chargement des dates…</div>}

        {cat && [1, 2].map((n) => parSemestre(n).length > 0 && (
          <section key={n} className="card-flat mt-8" data-testid={`workshop-semestre-${n}`}>
            <div className="font-display text-base md:text-lg mb-4">
              {n === 1 ? "Premier semestre" : "Second semestre"}
            </div>
            <div className="space-y-2">
              {parSemestre(n).map((s) => (
                <label key={s.id} className="flex items-start gap-3 p-3 rounded-xl cursor-pointer"
                  style={{ background: "var(--primary-tint)", opacity: auForfait(n) ? 0.5 : 1 }}>
                  <input type="checkbox" className="mt-1 accent-[var(--primary)]"
                    data-testid={`workshop-seance-${s.id}`}
                    disabled={auForfait(n)}
                    checked={seances.includes(s.id) || auForfait(n)}
                    onChange={() => basculerSeance(s.id)} />
                  <span className="text-sm flex-1">
                    {s.libelle}
                    {s.video && <span className="block text-xs italic">2h — vidéo incluse</span>}
                  </span>
                  <span className="font-display whitespace-nowrap">CHF {s.prix}.–</span>
                </label>
              ))}
              {(cat.forfaits || []).includes(n) && (
                <label className="flex items-start gap-3 p-3 rounded-xl cursor-pointer"
                  style={{ background: "#FFF8E7", border: "1px solid #F5D57A" }}>
                  <input type="checkbox" className="mt-1 accent-[var(--primary)]"
                    data-testid={`workshop-forfait-${n}`}
                    checked={auForfait(n)} onChange={() => basculerForfait(n)} />
                  <span className="text-sm flex-1" style={{ color: "#8B6914" }}>
                    <strong>Je m'inscris aux {parSemestre(n).length} workshops du semestre</strong>
                    <span className="block text-xs">
                      au lieu de CHF {parSemestre(n).reduce((t, s) => t + s.prix, 0)}.–
                    </span>
                  </span>
                  <span className="font-display whitespace-nowrap" style={{ color: "#8B6914" }}>
                    CHF {cat.forfait_prix}.–
                  </span>
                </label>
              )}
            </div>
          </section>
        ))}

        <section className="card-flat mt-8">
          <div className="font-display text-base md:text-lg mb-4">Tes coordonnées</div>
          {[["nom_complet", "Nom et prénom *", "text"], ["email", "Adresse e-mail *", "email"],
            ["telephone", "Numéro de téléphone *", "tel"]].map(([cle, label, type]) => (
            <label key={cle} className="block mb-4">
              <span className="text-sm font-medium">{label}</span>
              <input className="input-field mt-1" type={type} value={f[cle]}
                data-testid={`workshop-${cle}`}
                onChange={(e) => setF({ ...f, [cle]: e.target.value })} />
            </label>
          ))}
          <label className="block">
            <span className="text-sm font-medium">Un mot pour Noémie (facultatif)</span>
            <textarea className="input-field mt-1" rows={3} value={f.commentaire}
              data-testid="workshop-commentaire"
              onChange={(e) => setF({ ...f, commentaire: e.target.value })} />
          </label>
        </section>

        <div className="card-flat mt-8">
          <div className="flex items-center justify-between">
            <span className="font-display text-base md:text-lg">Total</span>
            <span className="font-display text-3xl" data-testid="workshop-total">CHF {total}.–</span>
          </div>
          <div className="mt-4 flex items-start gap-3 p-4 rounded-2xl"
            style={{ background: "#FFF8E7", border: "1px solid #F5D57A" }}>
            <Info size={16} className="mt-0.5 shrink-0" style={{ color: "#B7791F" }} />
            <div className="text-xs leading-relaxed" style={{ color: "#8B6914" }}>
              Le nombre de participantes est limité. <strong>Ton inscription n'est validée
              qu'une fois le montant total versé par TWINT{cat?.twint ? ` au ${cat.twint}` : ""}</strong> ;
              tant que tu n'as pas payé, ta place peut être prise. Une fois validée, elle n'est
              ni remboursable ni modifiable — tu peux par contre revendre ta place.
              Les workshops ont lieu {cat?.lieu ? `à la ${cat.lieu}` : "chez 1000 Arts"}.
            </div>
          </div>
          {etat.err && (
            <div className="mt-4 text-sm" style={{ color: "#B91C1C" }} data-testid="workshop-erreur">{etat.err}</div>
          )}
          <button className="btn-primary mt-5" disabled={!valide || etat.envoi}
            data-testid="workshop-envoyer" onClick={envoyer}>
            {etat.envoi ? <><Loader2 size={16} className="animate-spin" /> Envoi…</>
              : <><Check size={16} /> Valider mon inscription</>}
          </button>
          {!valide && (
            <div className="text-xs text-[var(--muted)] mt-2">
              Nom et prénom, e-mail, téléphone et au moins un workshop sont nécessaires.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
